/**
 * useChangelog — Hook für ungesehene Changelog-Einträge.
 *
 * Tracked auf localStorage (key: ivoliq_changelog_seen[_<audience>] — Array von IDs).
 * Berechnet `unseenCount` und `markAllSeen()` / `markSeen(id)`.
 *
 * Audience-aware (Feb 2026):
 *   - `audience`-Param ('fl' | 'sb', default 'fl') filtert Items nach Zielgruppe.
 *   - Items ohne `audience`-Feld sind für ALLE Rollen sichtbar.
 *   - Items mit `audience: 'fl'` nur für Freelancer, `audience: 'sb'` nur für Steuerberater.
 *   - Seen-State wird pro Audience separat gespeichert (FL und SB zählen unabhängig).
 *
 * Plan-Filter: Items mit plan_filter werden nur gezeigt, wenn user.plan/has_lifetime_access
 *         im Filter enthalten ist. (Items ohne plan_filter sind für alle.)
 *         Für SB greift der Plan-Filter NICHT (SB hat kein FL-Abo).
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import CHANGELOG from '@/data/changelog';

const LS_KEY_BASE = 'ivoliq_changelog_seen';
const lsKey = (audience) => (audience === 'sb' ? `${LS_KEY_BASE}_sb` : LS_KEY_BASE);

function readSeen(audience) {
  try {
    const raw = localStorage.getItem(lsKey(audience));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function writeSeen(audience, ids) {
  try { localStorage.setItem(lsKey(audience), JSON.stringify(ids)); } catch { /* noop */ }
}

function isVisibleForAudience(item, audience) {
  // Ohne audience-Feld = für alle Rollen sichtbar
  if (!item.audience) return true;
  return item.audience === audience;
}

function isVisibleForPlan(item, user) {
  if (!item.plan_filter || item.plan_filter.length === 0) return true;
  const plan = (user?.plan || 'free').toLowerCase();
  const lifetime = !!user?.has_lifetime_access;
  if (lifetime && item.plan_filter.includes('lifetime')) return true;
  return item.plan_filter.includes(plan);
}

export function useChangelog(audience = 'fl') {
  const { user } = useAuth();
  const [seenIds, setSeenIds] = useState(() => readSeen(audience));

  // Audience-Wechsel → Seen-State neu laden
  useEffect(() => {
    setSeenIds(readSeen(audience));
  }, [audience]);

  // Re-read auf Storage-Events (Multi-Tab)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === lsKey(audience)) setSeenIds(readSeen(audience));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [audience]);

  const visibleItems = CHANGELOG.filter((item) =>
    isVisibleForAudience(item, audience) &&
    // Plan-Filter nur für FL anwenden — SB hat kein FL-Abo
    (audience === 'sb' ? true : isVisibleForPlan(item, user))
  );
  const unseenItems = visibleItems.filter((item) => !seenIds.includes(item.id));
  const unseenCount = unseenItems.length;

  const markSeen = useCallback((id) => {
    setSeenIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      writeSeen(audience, next);
      return next;
    });
  }, [audience]);

  const markAllSeen = useCallback(() => {
    const allIds = visibleItems.map((i) => i.id);
    setSeenIds(allIds);
    writeSeen(audience, allIds);
  }, [visibleItems, audience]);

  return {
    items: visibleItems,
    unseenItems,
    unseenCount,
    markSeen,
    markAllSeen,
    isUnseen: (id) => !seenIds.includes(id),
  };
}

export default useChangelog;
